import { useTranslation } from 'react-i18next';
import { OpTextButton } from 'new-components/DLS/OpTextButton/OpTextButton';
import { useCallback, useEffect, useState } from 'react';
import message from 'antd/es/message';
import { requestResetPassword } from '../../helpers/requestResetPassword';
import { useAltaLoginUrlParams } from '../../hooks/useAltaLoginUrlParams';
import { ErrorAlert } from '../ErrorAlert/ErrorAlert';

import './ResetPasswordRequest.scss';

/**
 * @component
 * @description Component that sends the user an email to reset their password.
 * The user can also navigate back to the signin page or resend the reset password email.
 * @param {string} props.email - email for the account they are trying to reset.
 * @param {string} props.namespaceNickname - the namespace nickname for the account they are trying to reset.
 * @param {string} props.regionEnv - region env name of the namespace chosen.
 * @param {string} props.heliumEndpoint - endpoint for the current environment and chosen region to call /auth endpoints.
 * @param {string} props.onBackToSignIn - callback to navigate back to the signin page.
 * @returns {JSX.Element} React component
 */
export const ResetPasswordRequest = ({
  email,
  namespaceNickname,
  orgId,
  region,
  heliumEndpoint,
}: {
  email: string;
  namespaceNickname: string;
  orgId: string;
  region: OP.Env;
  heliumEndpoint: string;
}) => {
  const { t } = useTranslation();
  const { service } = useAltaLoginUrlParams();
  const [error, setError] = useState<string>();
  const [messageApi, contextHolder] = message.useMessage();

  const showSuccessMessage = useCallback(() => {
    messageApi.open({
      type: 'success',
      content: 'Email sent',
      className: 'reset-password-email__email-sent',
    });
  }, [messageApi]);

  const handleSendEmail = useCallback(async () => {
    const resetUrlParams = new URLSearchParams({
      email,
      orgId,
      region,
      namespace: namespaceNickname,
      resetPassword: 'true',
      ...(service && { service }),
    });

    const resetPasswordResponse = await requestResetPassword({
      email: email!,
      namespaceNickname,
      heliumEndpoint,
      resetUrlParams,
    });

    if (
      resetPasswordResponse.status >= 200 &&
      resetPasswordResponse.status < 300
    ) {
      showSuccessMessage();
    } else {
      const errorData = await resetPasswordResponse.json();

      setError(errorData.message);
    }
  }, [
    email,
    heliumEndpoint,
    namespaceNickname,
    orgId,
    region,
    service,
    showSuccessMessage,
  ]);

  // send the email when this component is rendered with new values only.
  useEffect(() => {
    handleSendEmail();
  }, [handleSendEmail]);

  return (
    <div className="reset-password-email">
      {error && <ErrorAlert message={error} />}
      <p className="reset-password-email__info">
        {t('If an account exists for ')}
        <span className="reset-password-email__email">{email}</span>
        {t(" we'll send instructions for resetting your password.")}
      </p>
      {contextHolder}
      <OpTextButton
        className="reset-password-email__resend-button"
        onClick={handleSendEmail}
      >
        {t('Send email again')}
      </OpTextButton>
    </div>
  );
};
